/**
 * RideDetailsModal — read-only Ride Details panel for the Control Room.
 * Surfaces AI-booking transparency (spoken vs matched address, confidence, AI
 * reasoning, transcript, corrections, source) and the dispatcher Handling
 * History, plus a one-tap Acknowledge action. Audit/display only — it never
 * touches the rides list layout or any dispatch / lifecycle / pricing logic.
 */
import React, { useState } from "react";
import {
  X, Wand2, Hand, CheckCircle2, MapPin, User, Globe, Phone, Mic,
  Crosshair, ChevronDown, ChevronUp, ArrowRight, Sparkles, Clock,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";
import { formatUkDateTime, scheduledTime } from "../../lib/formatTime";
import { toast } from "sonner";

const fmt = (iso) => formatUkDateTime(iso, iso || "—");

const SOURCE_LABELS = {
  "ai/voice": "Voice AI",
  "ai/website": "Website AI",
  "ai/passenger": "Passenger App AI",
};
const sourceLabel = (ride) =>
  SOURCE_LABELS[ride.source] || (ride.ai_drafted ? "AI Assistant" : (ride.source || "Manual"));

const ConfidencePill = ({ value }) => {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const tone = pct >= 90
    ? { dot: "#16A34A", bg: "#F0FDF4", bd: "#BBF7D0", fg: "#166534", label: "High" }
    : pct >= 60
      ? { dot: "#F59E0B", bg: "#FFFBEB", bd: "#FDE68A", fg: "#B45309", label: "Medium" }
      : { dot: "#EF4444", bg: "#FEF2F2", bd: "#FECACA", fg: "#B91C1C", label: "Low" };
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full border"
      style={{ background: tone.bg, borderColor: tone.bd, color: tone.fg }}
      data-testid="ai-confidence-pill"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
      AI confidence: {pct}% · {tone.label}
    </span>
  );
};

export const RideDetailsModal = ({ ride: initialRide, onClose }) => {
  const [ride, setRide] = useState(initialRide);
  const [showReason, setShowReason] = useState(false);
  const [acking, setAcking] = useState(false);
  if (!ride) return null;

  const h = ride.handling_audit || null;
  const meta = ride.ai_voice_meta || null;
  const isVoice = ride.source === "ai/voice";

  const acknowledge = async () => {
    setAcking(true);
    try {
      const { data } = await api.post(`/admin/rides/${ride.id}/acknowledge`);
      setRide((r) => ({ ...r, handling_audit: data.handling_audit }));
      toast.success(data.already ? "Already acknowledged" : "Booking acknowledged");
    } catch (e) {
      toast.error("Could not acknowledge this booking");
    } finally {
      setAcking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="ride-details-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-lg font-black">Ride details</h3>
              {ride.ai_drafted && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4338CA] border border-[#C7D2FE]">
                  <Wand2 className="w-2.5 h-2.5" /> AI
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground font-mono-tab mt-0.5">{ride.id}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary" data-testid="ride-details-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Source */}
          <div className="flex items-center justify-between">
            <div className="label-mini text-muted-foreground">Source</div>
            <span
              className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-md bg-[#0A0A0A] text-white"
              data-testid="ride-source-label"
            >
              {isVoice ? <Phone className="w-3 h-3" /> : <Wand2 className="w-3 h-3" />} {sourceLabel(ride)}
            </span>
          </div>

          {/* Core summary */}
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-muted-foreground" /> <span className="font-semibold">{ride.passenger_name || "—"}</span></div>
            <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5" /> <span>{ride.pickup_address} → {ride.dropoff_address}</span></div>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <span className="uppercase">{ride.payment_method}</span> · £{ride.fare || ride.estimated_fare} · {ride.distance_mi ?? ride.distance_km} mi
            </div>
            {scheduledTime(ride) ? (
              <div className="flex items-center gap-2 pt-1" data-testid="ride-scheduled-pickup">
                <Clock className="w-3.5 h-3.5 text-[#B45309]" />
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-[#FFFBEB] text-[#B45309] border border-[#FDE68A]">
                  Scheduled pickup: {formatUkDateTime(scheduledTime(ride))}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground" data-testid="ride-scheduled-pickup">
                <Clock className="w-3.5 h-3.5" /> Pickup: as soon as possible
              </div>
            )}
          </div>

          {/* AI transparency (voice) */}
          {isVoice && meta && (
            <div className="border-t border-border pt-4 space-y-3" data-testid="ai-voice-meta">
              <div className="flex items-center justify-between gap-2">
                <div className="label-mini text-muted-foreground flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI interpretation</div>
                <ConfidencePill value={meta.confidence} />
              </div>

              {meta.needs_review && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FEF2F2] border border-[#FECACA]" data-testid="ai-needs-review">
                  <span className="text-sm">⚠️</span>
                  <div className="text-xs text-[#B91C1C] leading-snug">
                    <span className="font-bold">Verify location:</span> the postcode the caller spoke didn't
                    match the geocoder result. The address shown is exactly as spoken — please confirm the
                    pickup/destination before the driver sets off.
                  </div>
                </div>
              )}

              {/* AI heard vs Matched to */}
              <div className="grid grid-cols-1 gap-2">
                <div className="rounded-lg border border-[#C7D2FE] bg-[#EEF2FF] px-3 py-2" data-testid="ai-heard-block">
                  <div className="label-mini text-[#4338CA] mb-1 flex items-center gap-1"><Mic className="w-3 h-3" /> AI heard</div>
                  <div className="text-sm font-semibold leading-snug">{meta.spoken_pickup || ride.pickup_address}</div>
                  <div className="flex items-center gap-1 text-[11px] text-[#4338CA] my-0.5"><ArrowRight className="w-3 h-3" /></div>
                  <div className="text-sm font-semibold leading-snug">{meta.spoken_dropoff || ride.dropoff_address}</div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2" data-testid="ai-matched-block">
                  <div className="label-mini text-muted-foreground mb-1 flex items-center gap-1">
                    <Crosshair className="w-3 h-3" /> Matched to {meta.pickup_provider && <span className="opacity-70">· {meta.pickup_provider}</span>}
                  </div>
                  <div className="text-xs leading-snug">{meta.matched_pickup || "—"}</div>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground my-0.5"><ArrowRight className="w-3 h-3" /></div>
                  <div className="text-xs leading-snug">{meta.matched_dropoff || "—"}</div>
                </div>
              </div>

              {/* AI corrections */}
              {Array.isArray(meta.corrections) && meta.corrections.length > 0 && (
                <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2" data-testid="ai-corrections">
                  <div className="label-mini text-[#B45309] mb-1">AI corrected</div>
                  <div className="space-y-1">
                    {meta.corrections.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="line-through text-muted-foreground">{c.from}</span>
                        <ArrowRight className="w-3 h-3 text-[#B45309]" />
                        <span className="font-semibold">{c.to}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expandable reasoning + transcript */}
              <button
                onClick={() => setShowReason((s) => !s)}
                className="w-full flex items-center justify-between text-xs font-semibold text-muted-foreground hover:text-foreground py-1"
                data-testid="ai-reasoning-toggle"
              >
                <span>AI reasoning & transcript</span>
                {showReason ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showReason && (
                <div className="space-y-3 rounded-lg bg-[#0A0A0A] text-[#E5E7EB] px-3 py-3" data-testid="ai-reasoning-body">
                  {Array.isArray(meta.transcript) && meta.transcript.length > 0 && (
                    <div>
                      <div className="label-mini text-[#9CA3AF] mb-1 flex items-center gap-1"><Mic className="w-3 h-3" /> Original transcript</div>
                      <div className="space-y-1 font-mono-tab text-[11px] leading-relaxed" data-testid="ai-transcript">
                        {meta.transcript.map((t, i) => (
                          <div key={i} className="text-[#D1D5DB]">“{t}”</div>
                        ))}
                      </div>
                    </div>
                  )}
                  {meta.reason && (
                    <div>
                      <div className="label-mini text-[#9CA3AF] mb-1">Reason</div>
                      <div className="text-[11px] leading-relaxed text-[#D1D5DB]">{meta.reason}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Handling History */}
          <div className="border-t border-border pt-4">
            <div className="label-mini text-muted-foreground mb-2 flex items-center gap-1.5">
              <Globe className="w-3 h-3" /> Handling history
            </div>
            {h ? (
              <div className="space-y-2" data-testid="ride-handling-history">
                {h.reference && (
                  <div className="text-[11px] font-mono-tab text-muted-foreground">Web request {h.reference}</div>
                )}
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="handling-booked-by">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">Booked by:</span>{" "}
                    <span className="font-semibold inline-flex items-center gap-1">
                      {h.booked_by === "voice-ai" && <Phone className="w-3 h-3 text-[#4338CA]" />}
                      {h.booked_by_name || "—"}
                    </span>
                    {h.booked_at && <span className="text-muted-foreground"> · {fmt(h.booked_at)}</span>}
                  </div>
                </div>
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FFFBEB] border border-[#FDE68A]" data-testid="handling-acknowledged-by">
                  <Hand className="w-3.5 h-3.5 text-[#B45309] mt-0.5 flex-shrink-0" />
                  <div className="text-sm flex-1">
                    <span className="text-muted-foreground">Acknowledged by:</span>{" "}
                    <span className="font-semibold">{h.acknowledged_by_name || "—"}</span>
                    {h.acknowledged_at && <span className="text-muted-foreground"> · {fmt(h.acknowledged_at)}</span>}
                    {!h.acknowledged_by && (
                      <div className="mt-2">
                        <Button
                          size="sm" onClick={acknowledge} disabled={acking}
                          className="h-7 text-xs bg-[#002FA7] hover:bg-[#002080]"
                          data-testid="ride-acknowledge-btn"
                        >
                          <Hand className="w-3 h-3 mr-1" /> {acking ? "Acknowledging…" : "Acknowledge"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic" data-testid="ride-handling-history-empty">
                No AI handling history — this ride wasn't created from an AI booking.
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end sticky bottom-0 bg-white">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default RideDetailsModal;
